import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext'; // Import AuthContext
import './login.css';
import LoginFooter from './loginFooter/loginFooter';
import { generateRandomString } from '../../utils/utils'; 

const Login = () => {
  const {isAuthenticated, loginError } = useAuth(); // Get authState and loginError from AuthContext
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/'); // Redirect to home page if authenticated
    }
  }, [isAuthenticated, navigate]);

	  const generateCodeChallenge = async (codeVerifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    const base64Digest = btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); // base64-url encoding
    return base64Digest;
  };

	  const handleLogin = async () => {
    if (typeof window !== 'undefined') {
      const newNonce = generateRandomString();
      const newState = generateRandomString();
      sessionStorage.setItem('state', newState);
      const response = await fetch('/api/config/logingov', {
        method: 'GET',
      });
      console.log('from login response', response);
      const data = await response.json();
      console.log('from login data', data);

      const baseUrl = data.MFA_EXTERNAL_AUTH_URL;
      const clientId = data.MFA_EXTERNAL_CLIENT_ID;
      const redirectUri = encodeURIComponent(data.MFA_EXTERNAL_REDIRECT_URL);
      const acrValues = encodeURIComponent(data.MFA_ACR_VALUES);
      const responseType = data.MFA_RESPONSE_TYPE;
      const prompt = data.MFA_PROMPT;
      const scope = data.MFA_SCOPE;
      const responseMode = data.MFA_RESPONSE_MODE;

      if (baseUrl && clientId && redirectUri && acrValues && responseType && prompt && scope && responseMode) {
        const loginGovUrl = `${baseUrl}?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}&acr_values=${acrValues}&prompt=${prompt}&response_mode=${responseMode}&nonce=${newNonce}&state=${newState}`;

        // Redirect to Login.gov (no PKCE)
        window.location.href = loginGovUrl;
      } else {
        const errorMessage = 'Environment variables for Login.gov are not properly set.';
        console.error(errorMessage);
      }
    }
  };

  const handleMyAccessLogin = async () => {
    if (typeof window !== 'undefined') {
      const newNonce = generateRandomString();
      const newState = generateRandomString();

      // Ensure codeVerifier length is between 43 and 128 characters
      const codeVerifier = generateRandomString(43, 128); // Generates a string with a length between 43 and 128 characters
      sessionStorage.setItem('state', newState);
      sessionStorage.setItem('code_verifier', codeVerifier); // Store code verifier for token exchange
      console.log("from login code_verifier ", codeVerifier);

      const codeChallenge = await generateCodeChallenge(codeVerifier); // Generate code challenge
      console.log("from login code challenge ", codeChallenge);
      const response = await fetch('/api/config/myaccess', {
        method: 'GET',
      });
      const data = await response.json();
      console.log('from login data', data);
      const myAccessUrl = data.MYACCESS_AUTH_URL;
      const clientId = data.MYACCESS_CLIENT_ID;
      const redirectUri = encodeURIComponent(data.MYACCESS_REDIRECT_URL);
      const scope = data.MYACCESS_SCOPE;
      const responseType = data.MYACCESS_RESPONSE_TYPE;

      if (myAccessUrl && clientId && redirectUri && scope && responseType) {
        const myAccessLoginUrl = `${myAccessUrl}?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}&nonce=${newNonce}&state=${newState}&code_challenge=${codeChallenge}&code_challenge_method=S256`;

        // Redirect to MyAccess (PKCE included)
        window.location.href = myAccessLoginUrl;
      } else {
        const errorMessage = 'Environment variables for MyAccess are not properly set.';
        console.error(errorMessage);
      }
    }
  };

  const containerRef = useRef(null);

	useEffect(() => {

		if (!document.getElementById("homepage-styles")) {
			const style = document.createElement("style");
			style.id = "homepage-styles";
			style.innerHTML = `
				body {
					margin: 0;
					font-family: Roboto, Arial, sans-serif;
					background-color: #f0f0f0;
				}

				.hp-page {
					padding: 16px;
				}

				.hp-content {
					margin: 0 auto;
					max-width: 70%;
				}

				.hp-spacer-2 { margin: 16px 0; }
				.hp-spacer-4 { margin: 32px 0; }

				.hp-cards-row {
					display: flex;
					gap: 16px;
					flex-wrap: wrap;
				}

				.hp-card-column {
					flex: 1 1 100%;
				}

				@media (min-width: 900px) {
					.hp-card-column {
						flex: 1 1 calc(50% - 8px);
					}
				}

				.hp-card {
					background: #ffffff;
					box-shadow: 0px 2px 4px rgba(0,0,0,0.1);
					border-radius: 4px;
					padding: 24px;
					height: 100%;
					display: flex;
					flex-direction: column;
					justify-content: space-between;
				}

				.hp-card-row {
					display: flex;
					flex-wrap: wrap;
				}

				.hp-logo-col {
					flex: 0 0 100%;
					margin-bottom: 16px;
				}

				.hp-text-col {
					flex: 0 0 100%;
				}

				@media (min-width: 900px) {
					.hp-logo-col {
						flex: 0 0 25%;
					}
					.hp-text-col {
						flex: 0 0 75%;
					}
				}

				.hp-logo-box {
					display: flex;
					align-items: center;
					justify-content: center;
					min-height: 120px;
				}

				.hp-logo-box img,
				.hp-logo-box svg {
					max-width: 100%;
					height: auto;
				}

				.hp-min-height {
					min-height: 175px;
				}

				.hp-button-container {
					display: flex;
					justify-content: center;
					margin-top: 16px;
				}

				.hp-button {
					min-width: 160px;
					padding: 8px 22px;
					font-size: 0.875rem;
					font-weight: 500;
					border: none;
					border-radius: 4px;
					cursor: pointer;
					background-color: #1976d2;
					color: white;
					transition: background-color 0.2s ease;
				}

				.hp-button:hover {
					background-color: #115293;
				}

				.hp-center-card {
					text-align: center;
				}

				.hp-warning-chip {
					display: inline-block;
					background-color: #bf360c;
					color: white;
					font-weight: bold;
					font-size: 1.15rem;
					min-width: 100px;
					padding: 6px 12px;
					border-radius: 16px;
					margin-bottom: 16px;
				}

				.hp-warning-text {
					text-align: left;
				}

				.hp-link {
					color: #1976d2;
					text-decoration: none;
				}

				.hp-link:hover {
					text-decoration: underline;
				}
			`;
			document.head.appendChild(style);
		}

		containerRef.current.innerHTML = `
			<div class="hp-page">
				<div class="hp-content">
				     <h2>Welcome to FCWD, the Foreign Convictions and Withdrawal Database.</h2>
          <p>
           FCWD is designed to reduce commercial motor vehicle (CMV)-related crashes and fatalities by entering convictions of violations in 49 CFR § 383.51 for foreign Commercial Drivers License (CDL) holders.
          </p>

		  ${loginError ? `
			<div class="hp-spacer-2"></div>
			<p class="error-message-login">Error: ${loginError}</p>
			` : ''}
					<div class="hp-spacer-4"></div>

					<div class="hp-cards-row">
						<div class="hp-card-column">
							<div class="hp-card">
								<div>
									<div class="hp-card-row">
										<div class="hp-logo-col">
											<div class="hp-logo-box">
												<img src="../../images/logingov.jpg" alt="login.gov">
											</div>
										</div>
										<div class="hp-text-col">
											<div class="hp-min-height">
												<h3>State Users</h3>
												<div class="hp-spacer-2"></div>
												<p>
													Select this option to authenticate through Login.gov.
													You will be redirected to the Login.gov site to enter your credentials.
												</p>
											</div>
										</div>
									</div>
								</div>
								<div class="hp-button-container">
									<button class="hp-button" id="loginGovBtn">
										Sign In Login.gov
									</button>
								</div>
							</div>
						</div>

						<div class="hp-card-column">
							<div class="hp-card">
								<div>
									<div class="hp-card-row">
										<div class="hp-logo-col">
											<div class="hp-logo-box">
												<svg xmlns="http://www.w3.org/2000/svg"
													viewBox="0 0 35.82 37.1"
													role="img"
													style="fill:#001647;">
													<title>United States Department of Transportation</title>
													<desc>U.S. Department of Transportation Icon</desc>
													<g>
														<g>
															<path d="M4.46,6.35a8.38,8.38,0,0,1,8.15-2.94c2.71.55,4.26,2.77,4.78,5.43a6.31,6.31,0,0,1-2.75,6.79c-3.68,2.1-8,3.74-8.86,8.5A13.42,13.42,0,0,0,8.47,34.4C3.81,31.8,1.13,26.6.25,21.23A18,18,0,0,1,4.46,6.35"></path>
															<path d="M14.41,36.71A8.65,8.65,0,0,1,7.85,30.9a6.51,6.51,0,0,1,2.13-7,5.85,5.85,0,0,1,7.05-1c3.61,2.23,7.17,5.31,11.56,3.64A12.82,12.82,0,0,0,35.81,19C36,24.49,33,29.5,29,33a16.57,16.57,0,0,1-14.55,3.72"></path>
															<path d="M34.73,12.39a9.12,9.12,0,0,1-1.53,8.8c-1.79,2.17-4.42,2.47-6.91,1.63A6.18,6.18,0,0,1,21.92,17c0-4.35.81-9.1-2.8-12.17A12.51,12.51,0,0,0,9.05,2.33c4.47-2.92,10.27-2.89,15.22-1A17.36,17.36,0,0,1,34.73,12.39"></path>
														</g>
													</g>
												</svg>
											</div>
										</div>
										<div class="hp-text-col">
											<div class="hp-min-height">
												<h3>FMCSA Users</h3>
												<div class="hp-spacer-2"></div>
												<p>
													Use this if you have a government email and PIV card.
													To access the FMCSA system area restricted to authorized personnel.
												</p>
											</div>
										</div>
									</div>
								</div>
								<div class="hp-button-container">
									<button class="hp-button" id="myAccessBtn">
										Sign In MyAccess
									</button>
								</div>
							</div>
						</div>
					</div>

					<div class="hp-spacer-2"></div>

					<div class="hp-card hp-center-card">
						<div>
							<div class="hp-warning-chip">Warning</div>
							<p class="hp-warning-text">
								You are accessing a U.S. Government information system. This information system, including all related 
								equipment, networks, and network devices, is provided for U.S. Government-authorized use only. Unauthorized 
								or improper use of this system is prohibited, and may result in civil and criminal penalties, or administrative 
								disciplinary action. The communications and data stored or transiting this system may be, for any lawful Government 
								purpose, monitored, recorded, and subject to audit or investigation. By using this system, you understand and consent to such terms.
							</p>
						</div>
					</div>
				</div>
			</div>
		`;

		document
			.getElementById("loginGovBtn")
			?.addEventListener("click", () => {
				handleLogin();
			});

		document
			.getElementById("myAccessBtn")
			?.addEventListener("click", () => {
				handleMyAccessLogin();
			});
	}, [handleMyAccessLogin, loginError]);

	return (
		<>
			<div ref={containerRef} />
			<LoginFooter />
		</>
	);
};

export default Login;
